/* eslint-disable max-len */
import {
  ColDef,
  EditableCallbackParams,
  ICellRendererParams,
  IFilterParams,
  ValueFormatterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { useSetRecoilState } from 'recoil';
import { EditLined, PauseLined, HourglassFullFilled } from '@tag/tag-icons';

import { Tooltip } from '@tag/tag-components-react-v4';

import {
  NOT_AVAILABLE_CHAR,
  STOCK_CODE_SEARCH_RESULTS_TO_SHOW,
} from '@/config/common.config';
import { getBillingTypes } from '@/utils/data-helpers/billingTypes';
import { getUnitPriceFriquencies } from '@/utils/data-helpers/unitPriceFriquencies';
import { NumberUtils } from '@/utils/numbers.utils';
import { bcValidator } from '../validation/validator';
import { ContractLineBCFilterComparators } from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/filters/ContractLineFilterComparators';
import { ContractLineFilterOptions } from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/filters/ContractLineFilterOptions';
import {
  dateCellRenderer,
  getCustomerLabel,
  getSpreadCustomer,
  isEditableBCContractLine,
  lastNextDayGap,
} from './helper';
import { CONTRACT_STATES_COLORS } from '@/config/statuses.config';
import CustomBillingInterval from '../components/CustomBillingInterval/CustomBillingInterval';
import ModalConfirmation from '../components/ModalConfirmation/ModalConfirmation';
import { Pill } from '@/components/common/Pill/Pill';
import HeaderCheckbox from '@/components/common/TableGrid/CustomComponents/AgGridCheckbox/AgGridHeaderCheckbox';
import RowCheckbox from '@/components/common/TableGrid/CustomComponents/AgGridCheckbox/AgGridRowCheckbox';
import { AgGridSelectableFilterOwnProps } from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';
import LineStartDateEditor from './LineStartDateEditor';
import ProRataEndDateEditor from './ProRataEndDateEditor';
import {
  ContractLineBillingCycleOutput,
  ContractStatusEnum,
  ContractOriginEnum,
  ProductOutputType,
  PurchaseOrderOutputType,
  PurchaseOrderStatusEnum,
  BillingTypeEnum as UpdateableBillingTypeEnum,
  ContractBillingStatusPascalCaseEnum,
  BillingTypeEnum,
} from '@/graphql/contracts/_generated_/contracts';
import { BCContractLinesInputFields } from '../hooks/useUpdateFullBCContractLine';
import { ModalWindowItem } from '@/components/ModalWindowApi/ModalWindowApiContext';
import { GetPaginatedFilteredProductsVariables } from '@/graphql/contracts/_generated_/getPaginatedFilteredProducts.query';
import { ContractStatusFormatted } from './variableContractLinescolumns';
import { ContractLineCommonData } from '@/graphql/contracts/_generated_/ContractLineCommonData.fragment';
import { EnvUtils } from '@/utils/env.utils';
import { UserRole } from '@/config/roles.config';
import { BulkUpdateContractLineInvoiceToCustomerVariables, BulkUpdateContractLineInvoiceToCustomer } from '@/graphql/contracts/_generated_/bulkUpdateContractLineInvoiceToCustomer.mutation';
import { BulkUpdateProRataEndDateVariables, BulkUpdateProRataEndDate } from '@/graphql/contracts/_generated_/bulkUpdateProRataEndDate.mutation';
import { BulkUpdateContractLineShipToCustomerVariables, BulkUpdateContractLineShipToCustomer } from '@/graphql/contracts/_generated_/bulkUpdateContractLineShipToCustomer.mutation';
import { PaginatedFilteredPurchaseOrders } from '../hooks/useGetPurchaseOrders';
import { clearLinesSelection } from '../components/ContractLinesGrid/linesSelectionObservable';
import { clearVariableLinesSelection } from '../components/ContractLinesGrid/variableLinesSelectionObservable';
import { rightsidePanel_isOpen } from '../../RightPanel/RightPanel.state';
import { isContractOneEvoMigrated } from '@/utils/oneEvoMigration.utils';

interface GetColumnsProps {
  contract: Contract | null;
  updateBCContractLine: (
    cl: ContractLineBillingCycleOutput,
    changedField: BCContractLinesInputFields,
    validationContext?: object
  ) => void;
  addModal: (newModal: ModalWindowItem) => UUID;
  getProducts: (params: GetPaginatedFilteredProductsVariables) => Promise<
  (
    | ({
      iD?: string | null;
      stockCode?: string | null;
      stockName?: string | null;
      productName?: string | null;
      productFamily?: string | null;
      productDivision?: string | null;
      defaultBillingType?: string | null;
    } & { __typename: 'ProductOutputType' })
    | null
  )[]
  >;
  billingIntervals: Pick<
  BillingInterval,
  'billingInterval' | 'formattedBillingInterval'
  >[];
  unitPriceFrequencies: UnitPriceFrequency[];
  billingTypes: BillingType[];
  searchShipToCustomer: {
    searchCustomers: (
      text: string
    ) => Promise<
    (ContractLineCommonData['shipToCustomer'] | undefined | null)[]
    >;
    isSearchLoading: boolean;
    searchCustomersData: Array<
    {
      customerID: string;
      customerName: string;
      customerAccountNumber: string;
      address?: string | null;
      postCode?: string | null;
    } & { __typename: 'CustomerType' }
    >;
  };
  searchInvoiceToCustomer: {
    searchCustomers: (
      text: string
    ) => Promise<
    (ContractLineCommonData['invoiceToCustomer'] | undefined | null)[]
    >;
    isSearchLoading: boolean;
    searchCustomersData: Array<
    {
      customerID: string;
      customerName: string;
      customerAccountNumber: string;
      address?: string | null;
      postCode?: string | null;
    } & { __typename: 'CustomerType' }
    >;
  };
  searchPurchaseOrders: {
    getPurchaseOrdersItems: (params: PaginatedFilteredPurchaseOrders) => Promise<(({
      id?: string | null;
      customerId?: string | null;
      customerName?: string | null;
      customerCode?: string | null;
      poNumber?: string | null;
      poDate?: any | null;
      poStartDate?: any | null;
      poEndDate?: any | null;
      poTotalValue?: string | null;
      poStatus?: PurchaseOrderStatusEnum | null;
      poNote?: string | null;
    } & {
    }) | null)[]>
  }
  bulkUpdateContractLineShipToCustomer: (
    params: BulkUpdateContractLineShipToCustomerVariables,
    isVariable: boolean,
    shouldRefetchCls: boolean
  ) => Promise<BulkUpdateContractLineShipToCustomer['bulkUpdateContractLineShipToCustomer'] | undefined>;
  bulkUpdateContractLineInvoiceToCustomer: (
    params: BulkUpdateContractLineInvoiceToCustomerVariables,
    isVariable: boolean,
    shouldRefetchCls: boolean
  ) => Promise<BulkUpdateContractLineInvoiceToCustomer['bulkUpdateContractLineInvoiceToCustomer'] | undefined>;
  bulkAssignPurchaseOrder: (
    purchaseOrderId: string,
    contractLineIds: string[],
    handleSuccess?: () => void
  ) => void;
  bulkUnassignPurchaseOrder: (
    contractLineIds: string[],
    handleSuccess?: () => void
  ) => void;
  bulkUpdateProRataEndDate: (
    params: BulkUpdateProRataEndDateVariables
  ) => Promise<BulkUpdateProRataEndDate['bulkUpdateProRataEndDate'] | undefined>;
  onOpenPauseContractLineModal?: (contractLine: ContractLineBillingCycleOutput) => void;
  onOpenDnrModal?: (contractLine: ContractLineBillingCycleOutput) => void;
  onOpenTerminatingModal?: (contractLine: ContractLineBillingCycleOutput) => void;
  userRole?: UserRole;
}

export function getBCContractLineColumns({
  contract,
  updateBCContractLine,
  addModal,
  getProducts,
  billingIntervals,
  unitPriceFrequencies,
  billingTypes,
  searchShipToCustomer,
  searchInvoiceToCustomer,
  searchPurchaseOrders,
  bulkUpdateContractLineShipToCustomer,
  bulkUpdateContractLineInvoiceToCustomer,
  bulkAssignPurchaseOrder,
  bulkUnassignPurchaseOrder,
  bulkUpdateProRataEndDate,
  onOpenPauseContractLineModal,
  onOpenDnrModal,
  onOpenTerminatingModal,
  userRole,
}: GetColumnsProps): ColDef<ContractLineBillingCycleOutput>[] {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);
  const isUserRole = userRole === UserRole.USER;
  const isReaderRole = userRole === UserRole.READER;
  const isOneEvoMigrated = isContractOneEvoMigrated(contract);
  const isRestrictedRole = isUserRole || isReaderRole || isOneEvoMigrated;
  const isEditableForUser = (params: any) => !isRestrictedRole && isEditableBCContractLine(params);

  return [
    {
      colId: 'grid-checkbox',
      headerName: '',
      headerComponent: HeaderCheckbox,
      minWidth: 50,
      maxWidth: 50,
      width: 50,
      flex: 0,
      resizable: false,
      sortable: false,
      cellClass: 'p-0',
      headerClass: 'p-0',
      lockPosition: true,
      pinned: 'left',
      lockPinned: true,
      suppressSizeToFit: true,
      cellRenderer: RowCheckbox,
    },
    {
      colId: 'status',
      headerName: 'Status',
      field: 'status',
      autoHeight: true,
      maxWidth: 140,
      lockPosition: true,
      resizable: false,
      pinned: 'left',
      suppressSizeToFit: true,
      filter: 'SelectableFilter',
      filterParams:
        (): AgGridSelectableFilterOwnProps<ContractLineBillingCycleOutput> => ({
          optionsOrGetterFunc:
            ContractLineFilterOptions.getStatusBCFilterOptions,
          comparator: ContractLineBCFilterComparators.statusComparator,
          rerenderOnNewRowsLoaded: true,
        }),
      cellStyle: () => ({ justifyContent: 'center', display: 'flex' }),
      cellRenderer: ({
        value,
        data,
      }: ICellRendererParams<
      ContractLineBillingCycleOutput,
      ContractStatusEnum
      >) => {
        const color = value ? CONTRACT_STATES_COLORS[value] : undefined;

        if (value === ContractStatusEnum.ActiveDNR && data?.statusChangeDate) {
          const dnrDate = new Date(data.statusChangeDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });

          const handleDnrClick = () => {
            if (data && onOpenDnrModal) {
              onOpenDnrModal(data);
            }
          };

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background={color ?? ''}
                      size="large"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleDnrClick}
                    >
                      {value ? ContractStatusFormatted[value] : ''}
                    </Pill>
                  </span>
                }
              >
                DNR Date: {dnrDate} - Click to view details
              </Tooltip>
            </div>
          );
        }

        if (value === ContractStatusEnum.Terminating) {
          const handleTerminatingClick = () => {
            if (data && onOpenTerminatingModal) {
              onOpenTerminatingModal(data);
            }
          };

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background={color ?? ''}
                      size="large"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleTerminatingClick}
                    >
                      {value ? ContractStatusFormatted[value] : ''}
                    </Pill>
                  </span>
                }
              >
                Terminating - Click to view details
              </Tooltip>
            </div>
          );
        }

        return (
          <Pill background={color ?? ''} size="large">
            {value ? ContractStatusFormatted[value] : ''}
          </Pill>
        );
      },
    },
    ...([{
      colId: 'pauseStatus',
      headerName: 'Pause Status',
      field: 'lineBillingStatus',
      autoHeight: true,
      maxWidth: 140,
      lockPosition: true,
      resizable: false,
      pinned: 'left' as const,
      suppressSizeToFit: true,
      filter: 'SelectableFilter',
      filterParams:
        (): AgGridSelectableFilterOwnProps<ContractLineBillingCycleOutput> => ({
          optionsOrGetterFunc:
            ContractLineFilterOptions.getPauseStatusBCFilterOptions,
          comparator: ContractLineBCFilterComparators.pauseStatusComparator,
          rerenderOnNewRowsLoaded: true,
        }),
      cellStyle: () => ({ justifyContent: 'center', display: 'flex' }),
      cellRenderer: ({
        data
      }: ICellRendererParams<ContractLineBillingCycleOutput>) => {
        if (data?.status != ContractStatusEnum.Active && data?.status != ContractStatusEnum.ActiveDNR && data?.status != ContractStatusEnum.Terminating) {
          return null;
        }

        const status = data?.lineBillingStatus;

        const handleEditClickForActive = () => {
          if (data && onOpenPauseContractLineModal) {
            onOpenPauseContractLineModal(data);
          }
        };

        if (status === ContractBillingStatusPascalCaseEnum.Paused) {
          const startDate = data?.lineBillingCurrentStatusStartDate
            ? new Date(data.lineBillingCurrentStatusStartDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';
          const endDate = data?.lineBillingCurrentStatusEndDate
            ? new Date(data.lineBillingCurrentStatusEndDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background="#9C27B0"
                      size="large"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleEditClickForActive}
                    >
                      Paused <PauseLined style={{ fill: 'white', height: '20px' }} />
                    </Pill>
                  </span>
                }
              >
                Paused - Click to view details
                <br />
                Start Date: {startDate}
                <br />
                End Date: {endDate}
              </Tooltip>
            </div>
          );
        }

        if (status === ContractBillingStatusPascalCaseEnum.FuturePause) {
          const startDate = data?.lineBillingCurrentStatusStartDate
            ? new Date(data.lineBillingCurrentStatusStartDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';
          const endDate = data?.lineBillingCurrentStatusEndDate
            ? new Date(data.lineBillingCurrentStatusEndDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background="#00BCD4"
                      size="large"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleEditClickForActive}
                    >
                      Future Pause <HourglassFullFilled style={{ fill: 'white', height: '20px' }} />
                    </Pill>
                  </span>
                }
              >
                Future Pause - Click to view details
                <br />
                Start Date: {startDate}
                <br />
                End Date: {endDate}
              </Tooltip>
            </div>
          );
        }

        if (status === ContractBillingStatusPascalCaseEnum.On) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background="#2196F3"
                      size="large"
                      style={{ cursor: isRestrictedRole ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={isRestrictedRole ? undefined : handleEditClickForActive}
                    >
                      Off {!isRestrictedRole && <EditLined style={{ fill: 'white', height: '20px' }} />}
                    </Pill>
                  </span>
                }
              >
                {isRestrictedRole ? 'Off' : 'Off - Click to pause'}
              </Tooltip>
            </div>
          );
        }

        return null;
      },
    } as ColDef<ContractLineBillingCycleOutput>]),
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'stockCode',
      initialWidth: 350,
      minWidth: 250,
      editable: isEditableForUser,
      cellEditor: 'ProductDropdownEditorAsync',
      cellEditorParams: ({ data }: ICellRendererParams) => ({
        handleInputChange: getProducts,
        searchResults: STOCK_CODE_SEARCH_RESULTS_TO_SHOW,
        billingTypes: [
          BillingTypeEnum.Subscription,
          BillingTypeEnum.Initials,
          BillingTypeEnum.Deposit,
        ],
        getOptionValue: (option: ProductOutputType) => option.stockCode,
        getOptionLabel: (option: ProductOutputType) =>
          `${option.stockCode} - ${option.stockName}`,
        placeholder: `${data.stockCode} - ${data.stockName}`,
        saveOnChange: true,
        autofocus: true,
      }),
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(colDef?.field!, data),
      }),
      valueSetter: ({ data, newValue }) => {
        const product = newValue as ProductOutputType;

        updateBCContractLine(data, {
          stockCode: product?.stockCode ?? undefined,
        });

        return false;
      },
      cellRenderer: ({ data }: ICellRendererParams) => {
        return `${data.stockCode} - ${data.stockName}`;
      },
    },
    {
      colId: 'quantity',
      headerName: 'Quantity',
      field: 'quantity',
      initialWidth: 110,
      minWidth: 110,
      cellStyle: () => ({ textAlign: 'right' }),
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        data.quantity = newValue;

        updateBCContractLine(data, {
          quantity: newValue,
        });

        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(colDef?.field!, data),
      }),
      valueParser: ({ newValue }) => {
        const quantity = NumberUtils.parseIntegerInput(String(newValue));
        return isNaN(quantity) ? newValue : quantity;
      },
      valueFormatter: ({
        value,
      }: ValueFormatterParams<ContractLineBillingCycleOutput>) => {
        return NumberUtils.prettifyWithRound(value, 3, ',', 0) ?? '';
      },
    },
    {
      colId: 'unitType',
      headerName: 'Unit Type',
      field: 'unitType',
      initialWidth: 120,
      minWidth: 120,
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        if (newValue?.trim()?.length || data?.unitType) {
          data.unitType = newValue;
          updateBCContractLine(data, {
            unitType: newValue,
          });
        }
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(colDef!.field!, data),
      }),
      valueParser: ({ newValue }) => {
        return typeof newValue === 'string' && newValue.length === 0
          ? null
          : newValue;
      },
    },
    {
      colId: 'unitPrice',
      headerName: 'Unit Price',
      field: 'unitPrice',
      initialWidth: 130,
      minWidth: 130,
      cellStyle: () => ({ textAlign: 'right' }),
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        const newUnitPrice = NumberUtils.parseNumericInput(String(newValue));
        data.unitPrice = newValue;

        updateBCContractLine(data, {
          unitPrice: newUnitPrice,
        });

        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(colDef?.field!, data),
      }),
      valueParser: ({ newValue }) => {
        const unitPrice = NumberUtils.parseNumericInput(String(newValue));
        return isNaN(unitPrice) ? newValue : unitPrice;
      },
      valueFormatter: ({
        value,
      }: ValueFormatterParams<ContractLineBillingCycleOutput>) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      },
    },
    {
      colId: 'billingPeriodValue',
      headerName: 'Billing Period Value',
      field: 'billingPeriodValue',
      initialWidth: 190,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      cellRenderer: ({ value }: ICellRendererParams) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      },
    },
    {
      colId: 'annualBillingPeriodValue',
      headerName: 'Annual Billing Value',
      field: 'annualBillingPeriodValue',
      initialWidth: 190,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      cellRenderer: ({
        value,
        data,
      }: ICellRendererParams<
      ContractLineBillingCycleOutput,
      ContractLineBillingCycleOutput['annualBillingPeriodValue']
      >) => {
        if (
          data?.billingType === 'Initials' ||
          data?.billingType === 'Deposit'
        ) {
          return NOT_AVAILABLE_CHAR;
        }

        return NumberUtils.prettifyWithRound(value) ?? '';
      },
      cellClassRules: {
        'ag-cell-gray': ({ value, data }) =>
          !value && data?.billingType !== 'Subscription',
      },
    },
    {
      colId: 'billingType',
      headerName: 'Billing Type',
      field: 'billingType',
      initialWidth: 170,
      minWidth: 160,
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }: ValueSetterParams) => {
        const billingType = newValue as BillingType;
        data.billingType =
          UpdateableBillingTypeEnum[
            billingType.type as keyof typeof UpdateableBillingTypeEnum
          ];

        updateBCContractLine(data, {
          unitPriceFrequency: data.unitPriceFrequency as any,
          billingInterval: data.billingInterval as any,
          billingType: billingType.type,
        });

        return true;
      },
      cellEditor: 'DropdownEditor',
      cellEditorParams: () => ({
        options: getBillingTypes(),
        getOptionValue: (option: BillingType): string => option.type,
        getOptionLabel: (option: BillingType): string => option.formattedType,
        saveOnChange: true,
      }),
      cellRenderer: ({
        value,
      }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {
        return (
          billingTypes.find((x) => x.type == value)?.formattedType ?? value
        );
      },
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(colDef?.field!, data),
      }),
      filter: 'SelectableFilter',
      filterParams: ({
        api,
      }: IFilterParams): AgGridSelectableFilterOwnProps<ContractLineBillingCycleOutput> => ({
        optionsOrGetterFunc:
          ContractLineFilterOptions.getBillingTypeBCFilterOptions(
            api,
            billingTypes
          ),
        comparator: ContractLineBCFilterComparators.billintTypeComparator,
      }),
      valueFormatter: ({
        value,
      }: ValueFormatterParams<ContractLineBillingCycleOutput, string>) => {
        return value || '';
      },
    },
    {
      field: 'unitPriceFrequency',
      colId: 'unitPriceFrequency',
      headerName: 'Unit Price Frequency',
      initialWidth: 170,
      minWidth: 150,
      editable: isEditableForUser,
      cellClassRules: {
        'ag-cell-gray': ({ value, data }) =>
          !value && data?.billingType !== 'Subscription',
      },
      valueSetter: ({ data, newValue }) => {
        const unitPriceFrequency = newValue as UnitPriceFrequency;
        data.unitPriceFrequency = unitPriceFrequency.frequency;

        updateBCContractLine(data, {
          unitPriceFrequency: unitPriceFrequency.frequency,
          billingInterval: data.billingInterval as any,
          billingType: data.billingType as any,
        });

        return true;
      },
      cellEditor: 'DropdownEditor',
      cellRenderer: 'CellRenderer',
      cellEditorParams: () => ({
        options: getUnitPriceFriquencies(),
        getOptionValue: (option: UnitPriceFrequency) => option.frequency,
        getOptionLabel: (option: UnitPriceFrequency) =>
          option.formattedFrequency,
        saveOnChange: true,
      }),
      cellRendererParams: ({
        colDef,
        data,
      }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => ({
        errors: bcValidator.getErrorsAt(colDef?.field!, data!),
      }),
      valueFormatter: ({
        value,
        data,
      }: ValueFormatterParams<ContractLineBillingCycleOutput, string>) => {
        if (
          !value &&
          (data?.billingType === 'Initials' || data?.billingType === 'Deposit')
        ) {
          return NOT_AVAILABLE_CHAR;
        }

        return value || '';
      },
      filter: 'SelectableFilter',
      filterParams: ({
        api,
      }: IFilterParams): AgGridSelectableFilterOwnProps<ContractLineBillingCycleOutput> => ({
        optionsOrGetterFunc:
          ContractLineFilterOptions.getUnitPriceFrequenciesBCOptions(
            api,
            unitPriceFrequencies
          ),
        comparator:
          ContractLineBCFilterComparators.unitPriceFrequencyComparator,
      }),
    },
    {
      colId: 'billingInterval',
      headerName: 'Billing Interval',
      field: 'billingInterval',
      initialWidth: 170,
      minWidth: 150,
      editable: isEditableForUser,
      cellClassRules: {
        'ag-cell-gray': ({ value, data }) =>
          !value && data?.billingType !== 'Subscription',
      },
      valueSetter: ({ data, newValue }) => {
        const billingInterval = newValue as BillingInterval;
        data.billingInterval = billingInterval.billingInterval;

        updateBCContractLine(data, {
          unitPriceFrequency: data.unitPriceFrequency as any,
          billingInterval: billingInterval.billingInterval,
          billingType: data.billingType as any,
        });

        return true;
      },
      cellEditor: 'DropdownEditor',
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({
        colDef,
        data,
      }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {
        return {
          errors: bcValidator.getErrorsAt(colDef?.field!, data!),
        };
      },
      valueFormatter: ({
        value,
        data,
      }: ValueFormatterParams<ContractLineBillingCycleOutput, string>) => {
        if (
          !value &&
          (data?.billingType === 'Initials' || data?.billingType === 'Deposit')
        ) {
          return NOT_AVAILABLE_CHAR;
        }

        return value || '';
      },
      cellEditorParams: ({ data }: { data: ContractLineBillingCycleOutput }) => {
        const isLinePaused = (
          data?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
          data?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
        );

        return {
          options: billingIntervals,
          getOptionValue: (option: BillingInterval): string =>
            option.billingInterval,
          getOptionLabel: (option: BillingInterval): string =>
            option.formattedBillingInterval,
          saveOnChange: true,
          handleChangeOption: (
            selectedOption: BillingInterval,
            applyChanges: (selectedOption: BillingInterval) => void,
            cancelChanges: () => void
          ) => {
            if (selectedOption.billingInterval === 'add-new-interval') {
              addModal({
                modalType: 'default',
                title: 'Add interval manually',
                onCloseCallback: cancelChanges,
                ModalContent: () => (
                  <CustomBillingInterval applyChanges={applyChanges} />
                ),
              });
            } else {
              addModal({
                modalType: 'confirmation',
                title: 'Please confirm you wish to continue',
                onCancelButtonParams: {
                  callback: cancelChanges,
                },
                onConfirmButtonParams: {
                  callback: () => applyChanges(selectedOption),
                },
                ModalContent: () => (
                  <ModalConfirmation fieldName="billing interval" hasPauseActive={isLinePaused} />
                ),
              });
            }
          },
        };
      },
      filter: 'SelectableFilter',
      filterParams: ({
        api,
      }: IFilterParams): AgGridSelectableFilterOwnProps<ContractLineBillingCycleOutput> => ({
        optionsOrGetterFunc:
          ContractLineFilterOptions.getBillingIntervalsBCOptions(
            api,
            billingIntervals
          ),
        comparator: ContractLineBCFilterComparators.billingIntervalComparator,
      }),
    },

    {
      colId: 'nextInvoiceDate',
      headerName: 'Next Invoice Date',
      field: 'nextInvoiceDate',
      initialWidth: 150,
      minWidth: 150,
      cellClassRules: {
        'ag-cell-gray': ({ value, data }) =>
          !value && data?.billingType !== 'Subscription',
      },
      cellRenderer: ({
        data,
        value,
      }: ICellRendererParams<
      ContractLineBillingCycleOutput,
      ContractLineBillingCycleOutput['nextInvoiceDate']
      >) => {
        if (
          data?.billingType === 'Initials' ||
          data?.billingType === 'Deposit'
        ) {
          return NOT_AVAILABLE_CHAR;
        }

        if (!value) return '';

        return dateCellRenderer({ value });
      },
    },
    ...(EnvUtils.isNextSoIssueDateEnabled() ? [{
      colId: 'nextSoIssueDate',
      headerName: 'Next SO Issue Date',
      field: 'nextSoIssueDate',
      initialWidth: 160,
      minWidth: 150,
      cellClassRules: {
        'ag-cell-gray': ({ value, data }: { value: any; data: any }) =>
          !value && data?.billingType !== 'Subscription',
      },
      cellRenderer: ({
        data,
        value,
      }: ICellRendererParams<
      ContractLineBillingCycleOutput,
      ContractLineBillingCycleOutput['nextSoIssueDate']
      >) => {
        if (
          data?.billingType === 'Initials' ||
          data?.billingType === 'Deposit'
        ) {
          return NOT_AVAILABLE_CHAR;
        }

        if (!value) return '';

        return dateCellRenderer({ value });
      },
    } as ColDef<ContractLineBillingCycleOutput>] : []),
    ...(!EnvUtils.isProRataFirstPeriodEnabled() ? [{
      colId: 'lineStartDate',
      headerName: 'Line Start Date',
      field: 'lineStartDate',
      initialWidth: 150,
      minWidth: 150,
      editable: isEditableForUser,
      cellEditor: LineStartDateEditor,
      cellEditorParams: {
        saveOnChange: true,
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: (params: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(params.colDef!.field!, params.data, {
          contract,
        }),
      }),
      valueSetter: ({ data, newValue, context }: ValueSetterParams) => {
        data.lineStartDate = newValue;
        updateBCContractLine(data, {
          lineStartDate: data.lineStartDate,
          changeReason: context.changeReason,
        });
        return true;
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value && params.data?.lineStartDate == null) {
          return NOT_AVAILABLE_CHAR;
        }
        return dateCellRenderer(params);
      },
    } as ColDef<ContractLineBillingCycleOutput>] : []),
    ...(EnvUtils.isProRataFirstPeriodEnabled() ? [{
      colId: 'proRataEndDate',
      headerName: 'Pro Rata End Date',
      field: 'proRataEndDate',
      initialWidth: 150,
      minWidth: 150,
      editable: (params: EditableCallbackParams) => {
        const effectiveOrigin = params.data?.origin ?? contract?.origin;
        return isEditableForUser(params)
          && params.data?.billingType === BillingTypeEnum.Subscription
          && effectiveOrigin !== ContractOriginEnum.Dimensions;
      },
      cellEditor: ProRataEndDateEditor,
      cellEditorParams: {
        saveOnChange: true,
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: (params: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(params.colDef!.field!, params.data, {
          contract,
        }),
      }),
      valueSetter: ({ data, newValue, api }: ValueSetterParams) => {
        const oldValue = data.proRataEndDate;
        data.proRataEndDate = newValue;
        bulkUpdateProRataEndDate({
          contractId: data.contractId,
          contractLineIds: [data.id],
          proRataEndDate: newValue
        }).then((result) => {
          if ((result?.errors?.length ?? 0) > 0) {
            data.proRataEndDate = oldValue;
            api.refreshCells({ rowNodes: [api.getRowNode(data.id)!], columns: ['proRataEndDate'], force: true });
          }
        }).catch(() => {
          data.proRataEndDate = oldValue;
          api.refreshCells({ rowNodes: [api.getRowNode(data.id)!], columns: ['proRataEndDate'], force: true });
        });
        return true;
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value && params.data?.proRataEndDate == null) {
          return NOT_AVAILABLE_CHAR;
        }
        return dateCellRenderer(params);
      },
    } as ColDef<ContractLineBillingCycleOutput>] : []),
    {
      colId: 'salesHandoverNumber',
      headerName: 'Sales Handover Number',
      field: 'salesHandoverNumber',
      initialWidth: 190,
      minWidth: 150,
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        if (newValue?.trim()?.length || data.salesHandoverNumber) {
          data.salesHandoverNumber = newValue;
          updateBCContractLine(data, {
            salesHandoverNumber: newValue,
          });
        }
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(colDef!.field!, data),
      }),
      valueParser: ({ newValue }) => {
        return typeof newValue === 'string' && newValue.length === 0
          ? null
          : newValue;
      },
    },
    {
      colId: 'comments',
      headerName: 'Comments',
      field: 'comments',
      initialWidth: 140,
      minWidth: 140,
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        if (newValue?.trim()?.length || data?.comments) {
          data.comments = newValue;
          updateBCContractLine(data, {
            comments: newValue,
          });
        }
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: bcValidator.getErrorsAt(colDef!.field!, data),
      }),
      valueParser: ({ newValue }) => {
        return typeof newValue === 'string' && newValue.length === 0
          ? null
          : newValue;
      },
    },
    {
      colId: 'lastBillingStartDate',
      headerName: 'Last Billing Start Date',
      field: 'lastBillingStartDate',
      initialWidth: 150,
      minWidth: 150,
      editable: false,
      cellRenderer: 'CellRenderer',
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value && params.data?.lastBillingStartDate == null) {
          return NOT_AVAILABLE_CHAR;
        }
        return dateCellRenderer(params);
      },
    },
    {
      colId: 'lastBillingEndDate',
      headerName: 'Last Billing End Date',
      field: 'lastBillingEndDate',
      initialWidth: 150,
      minWidth: 150,
      editable: false,
      cellRenderer: 'CellRenderer',
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value && params.data?.lastBillingEndDate == null) {
          return NOT_AVAILABLE_CHAR;
        }
        return dateCellRenderer(params);
      },
    },
    {
      colId: 'lastBillingDate',
      headerName: 'Last Billing Date',
      field: 'lastBillingDate',
      initialWidth: 150,
      minWidth: 150,
      editable: false,
      cellRenderer: 'CellRenderer',
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value && params.data?.lastBillingDate == null) {
          return NOT_AVAILABLE_CHAR;
        }
        return dateCellRenderer(params);
      },
    },
    {
      colId: 'nextBillingStartDate',
      headerName: 'Next Billing Start Date',
      field: 'nextBillingStartDate',
      initialWidth: 150,
      minWidth: 150,
      editable: false,
      cellRenderer: ({
        value,
        data,
      }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {

        let displayValue = '';

        if (!value && data?.nextBillingStartDate == null) {
          displayValue = NOT_AVAILABLE_CHAR;
        }

        let dayGap = lastNextDayGap(data);
        displayValue = dateCellRenderer({ value });

        return (<>
          {(dayGap > 1) &&
            <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
              There is a {dayGap}-day gap between the last and next billed periods.
            </Tooltip>}
          {displayValue}
        </>);
      },
    },
    {
      colId: 'nextBillingEndDate',
      headerName: 'Next Billing End Date',
      field: 'nextBillingEndDate',
      initialWidth: 150,
      minWidth: 150,
      editable: false,
      cellRenderer: ({
        value,
        data,
      }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {

        let displayValue = '';

        if (!value && data?.nextBillingEndDate == null) {
          return NOT_AVAILABLE_CHAR;
        }

        let dayGap = lastNextDayGap(data);
        displayValue = dateCellRenderer({ value });

        return (<>
          {(dayGap > 1) &&
            <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
              There is a {dayGap}-day gap between the last and next billed periods.
            </Tooltip>}
          {displayValue}
        </>);
      },
    },
    ...(EnvUtils.isContractLineMergeEnabled() ? [{
      colId: 'isFromMerge',
      headerName: 'Is From Merge',
      field: 'isFromMerge',
      initialWidth: 140,
      minWidth: 120,
      editable: false,
      cellRenderer: ({ value }: ICellRendererParams<ContractLineBillingCycleOutput, boolean>) => {
        return value ? 'Yes' : 'No';
      },
    },
    {
      colId: 'mergedLineId',
      headerName: 'Merged Line ID',
      field: 'mergedLineId',
      initialWidth: 300,
      minWidth: 200,
      editable: false,
      cellRenderer: 'CellRenderer',
    } as ColDef<ContractLineBillingCycleOutput>] : []),
    ...([
      {
        colId: 'shipToCustomer',
        headerName: 'Ship To Account',
        field: 'shipToCustomerId',
        initialWidth: 350,
        minWidth: 250,
        editable: isEditableForUser,
        cellEditor: 'CustomerDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchShipToCustomer.searchCustomers,
          searchResults: STOCK_CODE_SEARCH_RESULTS_TO_SHOW,
          getOptionValue: (
            option:
            | ContractLineCommonData['shipToCustomer']
            | undefined
            | null
          ) => option,
          getOptionLabel: (
            option:
            | ContractLineCommonData['shipToCustomer']
            | undefined
            | null
          ) => getCustomerLabel(option),
          placeholder: getCustomerLabel(
            getSpreadCustomer(data, 'shipToCustomer')
          ),
          saveOnChange: true,
          autofocus: true,
          isClearable: true,
          baseCurrentOption: getSpreadCustomer(data, 'shipToCustomer'),
        }),

        valueSetter: ({ data, newValue }) => {
          const shipToCustomer = newValue as
            | ContractLineCommonData['shipToCustomer']
            | undefined
            | null;

          if (shipToCustomer) {
            data.shipToCustomerId = shipToCustomer.customerID;
            data.shipToCustomerAccountNumber =
              shipToCustomer.customerAccountNumber;
            data.shipToCustomerName = shipToCustomer.customerName;
            data.shipToCustomerAddress = shipToCustomer.address;
            data.shipToCustomerPostcode = shipToCustomer.postCode;

            bulkUpdateContractLineShipToCustomer({
              shipToCustomerId: shipToCustomer?.customerID,
              contractLineIds: [data.id!],
            }, false, true);
          }

          return true;
        },
        cellRenderer: ({ data }: ICellRendererParams) => {
          return (
            <span className="c-ship-to">
              {getCustomerLabel(getSpreadCustomer(data, 'shipToCustomer'))}
            </span>
          );
        },
      },
      {
        colId: 'invoiceToCustomer',
        headerName: 'Invoice To Account',
        field: 'invoiceToCustomerId',
        initialWidth: 350,
        minWidth: 250,
        editable: isEditableForUser,
        cellEditor: 'CustomerDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchInvoiceToCustomer.searchCustomers,
          searchResults: STOCK_CODE_SEARCH_RESULTS_TO_SHOW,
          getOptionValue: (
            option:
            | ContractLineCommonData['invoiceToCustomer']
            | undefined
            | null
          ) => option,
          getOptionLabel: (
            option:
            | ContractLineCommonData['invoiceToCustomer']
            | undefined
            | null
          ) => getCustomerLabel(option),
          placeholder: getCustomerLabel(
            getSpreadCustomer(data, 'invoiceToCustomer')
          ),
          saveOnChange: true,
          autofocus: true,
        }),
        valueSetter: ({ data, newValue }) => {
          const invoiceToCustomer = newValue as
            | ContractLineCommonData['invoiceToCustomer']
            | undefined
            | null;

          if (invoiceToCustomer) {
            data.invoiceToCustomerId = invoiceToCustomer.customerID;
            data.invoiceToCustomerAccountNumber =
              invoiceToCustomer.customerAccountNumber;
            data.invoiceToCustomerName = invoiceToCustomer.customerName;
            data.invoiceToCustomerAddress = invoiceToCustomer.address;
            data.invoiceToCustomerPostcode = invoiceToCustomer.postCode;

            bulkUpdateContractLineInvoiceToCustomer({
              invoiceToCustomerId: invoiceToCustomer.customerID,
              contractLineIds: [data.id!],
            }, false, true);
            clearLinesSelection();
            clearVariableLinesSelection();
          }
          return true;
        },
        cellRenderer: ({ data }: ICellRendererParams) => {
          return (
            <span className="c-invoice-to">
              {getCustomerLabel(getSpreadCustomer(data, 'invoiceToCustomer'))}
            </span>
          );
        },
      },
      {
        colId: 'poNumber',
        headerName: 'PO Number',
        field: 'poNumber',
        initialWidth: 200,
        minWidth: 200,
        editable: (params: any) => !isReaderRole && !isOneEvoMigrated && isEditableBCContractLine(params),
        cellRenderer: ({ data }: ICellRendererParams) => {
          return data.poNumber;
        },
        cellEditor: 'PurchaseOrderDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchPurchaseOrders.getPurchaseOrdersItems,
          customerId: data.invoiceToCustomerId,
          customerCode: data.invoiceToCustomerAccountNumber,
          customerName: data.invoiceToCustomerName,
          currency: contract?.currency.currency,
          getOptionValue: (
            option:
            PurchaseOrderOutputType
            | undefined
            | null
          ) => option,
          getOptionLabel: (
            option:
            | PurchaseOrderOutputType
            | undefined
            | null
          ) => option?.poNumber,
          placeholder: data.poNumber,
          saveOnChange: true,
          autofocus: true,
        }),
        valueSetter: ({ data, newValue }) => {
          if (newValue && !(newValue as any).__cleared) {
            bulkAssignPurchaseOrder(newValue.id, [data.id!], () => {
              clearLinesSelection();
              clearVariableLinesSelection();
              setIsOpenRightPanel(false);
            });
          }
          else if (newValue && (newValue as any).__cleared) {
            bulkUnassignPurchaseOrder([data.id!], () => {
              clearLinesSelection();
              clearVariableLinesSelection();
              setIsOpenRightPanel(false);
            });
          }
          return true;
        }
      },
      {
        colId: 'poStatus',
        headerName: 'PO Status',
        field: 'poStatus',
        initialWidth: 190,
        minWidth: 150,
        editable: false,
        cellRenderer: ({ value }: ICellRendererParams<ContractLine, ContractLine['poStatus']>) =>
          !!value && value !== 'Active' ? <span>{'\u26A0\uFE0F ' + value}</span> : <span>{value}</span>,
      },
      {
        colId: 'oppHeaderId',
        headerName: 'Opp Header Id',
        field: 'oppHeaderId',
        initialWidth: 190,
        minWidth: 150,
        editable: false,
        cellRenderer: 'CellRenderer',
      },
      {
        colId: 'oppLineId',
        headerName: 'Opp Line Id',
        field: 'oppLineId',
        initialWidth: 190,
        minWidth: 150,
        editable: false,
        cellRenderer: 'CellRenderer',
      },
      {
        colId: 'bundleDescription',
        headerName: ' ',
        field: 'bundleDescription',
        rowGroup: true,
        hide: true,
        sort: 'asc',
        autoHeight: true,
      },
      {
        colId: 'id',
        headerName: ' ID',
        field: 'id',
        initialWidth: 300,
        editable: false,
        cellRenderer: 'CellRenderer',
      },
    ] as ColDef<ContractLineBillingCycleOutput>[]),
    {
      colId: 'origin',
      headerName: 'Origin',
      field: 'origin',
      initialWidth: 130,
      editable: false,
      cellRenderer: 'CellRenderer',
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ] as ColDef<ContractLineBillingCycleOutput>[];
}
